import React, { useCallback, useEffect, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classnames from 'classnames';
import { Theme } from '@app/theme';

import { NotFound } from '../NotFound';
import { CenteredLoader } from '../Loader';
import { HeaderConnect } from './HeaderConnect';
import { Participation, useMe, useParticipant } from '@app/api';
import { FooterStudy } from '../FooterStudy';
import { ParticipantMessagingPane } from '../../../../apps/participant/src/pages/ParticipationSummary/components/ParticipantMessagingPane';
import { WithRightClosablePane } from '../PageLayout';
import { useLocation, useNavigate } from 'react-router-dom';
import { ProgressBar } from '../ProgressBar';

const spaceBottom = 4;

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  mainContainer: {
    minHeight: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.colors.grayBackground,
  },
  container: {
    position: 'relative',
    flex: 1,
    display: 'flex',
    width: '100%',
    height: '100%',

    [theme.media.mobile]: {
      padding: 0,
      backgroundColor: theme.colors.white,
      height: 'auto',
      overflow: 'auto',
    },
  },
  withRightClosablePaneMainContent: {
    height: '100%',
  },
  withRightClosablePaneContainer: {
    paddingTop: theme.marginBase * 2,
    paddingBottom: theme.marginBase * spaceBottom,
    [theme.media.tablet]: {
      padding: [[theme.marginBase * 4, theme.marginBase * 2]],
    },
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.media.tablet]: {
      display: 'block',
    },
  },
  content: {
    position: 'relative',
    zIndex: 1,
    backgroundColor: theme.colors.white,

    width: '100%',
    maxWidth: theme.maxContentWidth.screen,

    margin: 'auto',
    padding: theme.marginBase * 10,
    boxShadow: theme.boxShadow.base,
    borderRadius: theme.borderRadius.large,

    [theme.media.tablet]: {
      padding: [[theme.marginBase * 8, theme.marginBase * 4]],
    },
    [theme.media.mobile]: {
      padding: [[theme.marginBase * 4, theme.marginBase * 2]],
      boxShadow: 'none',
      maxWidth: '100%',
    },
  },
  separator: {
    flex: 1,
  },
  loadingPlaceholder: {
    height: 600,
  },
  mainContent: {
    width: '100%',
  },
  closableMainContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.marginBase * 2,

    [theme.media.mobile]: {
      padding: 0,
    },
  },
  closablePane: {
    paddingTop: theme.marginBase * 2,
    paddingBottom: theme.marginBase * 2,
    background: 'transparent',

    [`@media (max-width: 1300px)`]: {
      width: 384,
      position: 'absolute',
      top: 0,
      bottom: 0,
      right: 0,
      zIndex: 1,
    },
    [theme.media.tablet]: {
      boxShadow: 'none',
    },
    [theme.media.mobile]: {
      paddingTop: 0,
      width: '100%',
    },
  },
  progressBar: {
    marginBottom: theme.marginBase * 8,
  },

  formContent: {
    maxWidth: theme.maxContentWidth.reduced,
    margin: 'auto',
  },
}));

interface Props {
  isLoading?: boolean;
  children?: React.ReactNode;
  tagsChildren?: React.ReactNode;
  bottomChildren?: React.ReactNode;
  customContent?: boolean;
  title?: string;
  withMessagesPane?: boolean;
  participation?: Participation;
  noLogin?: boolean;
  noHeader?: boolean;

  progression?: number;
  pageCount?: number;
  formContent?: boolean;
}

export const PublicBackground = ({
  isLoading,
  children,
  tagsChildren,
  bottomChildren,
  customContent = false,
  title,
  withMessagesPane,
  participation,
  noLogin,
  noHeader,
  progression = 0,
  pageCount = 0,
  formContent,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { data: me } = useMe(true);
  const { data: participant } = useParticipant(
    me?.linkedParticipants?.length ? me.linkedParticipants[0] : undefined,
  );
  const user = participant || me;

  const location = useLocation();
  const tab = new URLSearchParams(location.search).get('tab');
  const navigate = useNavigate();
  const openMessages = useCallback(() => {
    if (tab === 'messages') {
      navigate(location.pathname);
    } else {
      navigate(location.pathname + '?tab=messages');
    }
  }, [navigate, location, tab]);

  const content = formContent ? (
    <div className={classes.formContent}>{children}</div>
  ) : (
    children
  );

  return (
    <div
      className={classnames(classes.mainContainer, {
        [classes.withRightClosablePaneMainContent]: withMessagesPane,
      })}
    >
      {!noHeader && (
        <HeaderConnect
          user={user}
          title={title}
          tagsChildren={tagsChildren}
          bottomChildren={bottomChildren}
          openModalMessages={withMessagesPane ? openMessages : undefined}
          messagesOpened={tab === 'messages'}
          participationId={participation?.id}
          noLogin={noLogin}
        />
      )}
      <div
        className={classnames(classes.container, {
          [classes.withRightClosablePaneContainer]: !withMessagesPane,
        })}
      >
        <div
          className={classnames(classes.mainContent, {
            [classes.content]: !customContent && !withMessagesPane,
          })}
        >
          {pageCount > 0 && (
            <div className={classes.progressBar}>
              <ProgressBar progression={progression} fullSize={pageCount} />
            </div>
          )}

          {isLoading ? (
            <div className={classes.loadingPlaceholder}>
              <CenteredLoader />
            </div>
          ) : (
            (withMessagesPane && participation ? (
              <WithRightClosablePane
                pane={() => (
                  <ParticipantMessagingPane participation={participation} />
                )}
                isOpen={tab === 'messages'}
                onRequestClose={() => null}
                mainContentClassName={classes.closableMainContent}
                paneClassName={classes.closablePane}
                disablePaneOnClose
              >
                {customContent && content}
                {!customContent && (
                  <div className={classes.content}>{content}</div>
                )}

                <FooterStudy />
              </WithRightClosablePane>
            ) : (
              content
            )) || <NotFound />
          )}
        </div>
      </div>
      {!withMessagesPane && <FooterStudy />}
    </div>
  );
};
